import React from "react"
import { useRouteError } from "react-router"
import {
  getErrorCode,
  getErrorKey,
  GraphQLRequestError,
} from "@myvp/shared/src/functions/get-error-code"
import MessageDialog from "@myvp/shared/src/components/message-dialog"
import { getRum } from "@myvp/shared/src/cloudwatch-RUM"
import { ClientError } from "graphql-request"

export class GlobalAppError extends Error {
  errorCause
  constructor(error: unknown) {
    super("GlobalAppError")
    this.name = "GlobalAppError"
    this.errorCause = error
  }
}

export interface ErrorObject {
  title: React.ReactNode
  message: React.ReactNode
  displayIcon?: boolean
  onClose?: (...args: unknown[]) => unknown
  onCloseText?: string
  onConfirm?: (...args: unknown[]) => unknown
  onConfirmText?: string
  visible?: boolean
  disabled?: boolean
  redirect?: string | { pathname: string; search: string }
  variant?: React.ComponentProps<typeof MessageDialog>["variant"]
  render?: () => React.ReactNode
}
type Error<Metadata = object> =
  | ((args: { metadata?: Metadata }) => ErrorObject)
  | ErrorObject
type Errors<Metadata = object> = {
  [errorCode: string]: Error<Metadata>
} & { response?: GraphQLRequestError["response"] }

/**
 * If you refresh a page while network requests are loading in firefox it
 * cancels the requests and throws errors for each request. Triggering a flash of the
 * error fallback experience.
 */
let isUnloading = false
window.addEventListener("beforeunload", () => {
  isUnloading = true
})
export const useErrorBoundary = (
  errorConfigs: Errors,
  args: { readKey?: boolean } = {}
) => {
  let error = useRouteError()

  React.useEffect(() => {
    if (error) {
      getRum().then((rumUtils) => {
        if (rumUtils) {
          if (error instanceof ClientError || error instanceof Response) {
            // let request.ts handle logging these as events
            return
          }
          if ((error as any)?.data?.includes?.("Error: No route matches")) {
            // Do nothing, we'll still know that a user is ending up on 404s by looking at "performance events"
          } else {
            // Only log unhandled client errors, network request errors are logged as events
            rumUtils.logCustomError(error)
          }
        }
      })
    }
  }, [error])

  if (isUnloading) return null
  error = error instanceof GlobalAppError ? error.errorCause : error
  // oxlint-disable-next-line no-console
  console.log(error)
  // Backend may supply a custom error in form of an error key, so try the error key first
  const errorKeyConfig = args?.readKey
    ? errorConfigs[getErrorKey(error as any) as string]
    : null
  const errorCodeConfig = errorConfigs[getErrorCode(error as any) as string]
  let errorConfig = errorKeyConfig ?? errorCodeConfig ?? errorConfigs.fallback

  if (typeof errorConfig === "function") {
    let metadata
    try {
      metadata = JSON.parse(JSON.stringify(error))?.response?.errors?.[0]
        ?.extensions
    } catch {}
    errorConfig = errorConfig({ metadata })
  }
  return errorConfig
}
